import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Folder, Plus, Pencil, Trash2, Users, User, 
  CheckCircle2, FolderOpen, Settings, FolderPlus, ChevronRight, X
} from 'lucide-react';
import api, { projetService } from '../services/api';

const ProjetsManager = () => {
  const [projets, setProjets] = useState([]);
  const [animateurs, setAnimateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProjet, setEditingProjet] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // États pour les dossiers
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [showDossierDialog, setShowDossierDialog] = useState(false);
  const [editingDossier, setEditingDossier] = useState(null);
  const [dossierFormData, setDossierFormData] = useState({ nom: '', description: '' });
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    intervenants_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projetsRes, animateursRes] = await Promise.all([
        api.get('/projets'),
        api.get('/animateurs')
      ]);
      setProjets(projetsRes.data || []);
      setAnimateurs(animateursRes.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingProjet(null);
    setFormData({
      nom: '',
      description: '',
      intervenants_ids: []
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (projet) => {
    setEditingProjet(projet);
    setFormData({
      nom: projet.nom || '',
      description: projet.description || '',
      intervenants_ids: projet.intervenants_ids || []
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) {
      toast.error('Le nom du projet est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      if (editingProjet) {
        await api.put(`/projets/${editingProjet.id}`, formData);
        toast.success('Projet mis à jour');
      } else {
        await api.post('/projets', formData);
        toast.success('Projet créé');
      }
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projetId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ? Cette action est irréversible.')) {
      return;
    }

    try {
      await api.delete(`/projets/${projetId}`);
      toast.success('Projet supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleAnimateur = (animateurId) => {
    setFormData(prev => {
      const ids = prev.intervenants_ids || [];
      if (ids.includes(animateurId)) {
        return { ...prev, intervenants_ids: ids.filter(id => id !== animateurId) };
      } else {
        return { ...prev, intervenants_ids: [...ids, animateurId] };
      }
    });
  };

  const getAnimateurNom = (animateurId) => {
    const animateur = animateurs.find(a => a.id === animateurId);
    return animateur ? `${animateur.prenom} ${animateur.nom}` : 'Inconnu';
  };

  // === Fonctions pour les dossiers ===
  const loadDossiers = async (projetId) => {
    setLoadingDossiers(true);
    try {
      const response = await projetService.getDossiers(projetId);
      setDossiers(response.data || []);
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
      toast.error('Erreur lors du chargement des dossiers');
    } finally {
      setLoadingDossiers(false);
    }
  };

  const handleSelectProjet = async (projet) => {
    if (selectedProjet?.id === projet.id) {
      setSelectedProjet(null);
      setDossiers([]);
    } else {
      setSelectedProjet(projet);
      await loadDossiers(projet.id);
    }
  };

  const handleOpenCreateDossier = () => {
    setEditingDossier(null);
    setDossierFormData({ nom: '', description: '' });
    setShowDossierDialog(true);
  };

  const handleOpenEditDossier = (dossier) => {
    setEditingDossier(dossier);
    setDossierFormData({ nom: dossier.nom, description: dossier.description || '' });
    setShowDossierDialog(true);
  };

  const handleSubmitDossier = async () => {
    if (!dossierFormData.nom.trim()) {
      toast.error('Le nom du dossier est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      if (editingDossier) {
        await projetService.updateDossier(selectedProjet.id, editingDossier.id, dossierFormData);
        toast.success('Dossier mis à jour');
      } else {
        await projetService.createDossier(selectedProjet.id, dossierFormData);
        toast.success('Dossier créé');
      }
      setShowDossierDialog(false);
      await loadDossiers(selectedProjet.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDossier = async (dossierId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce dossier ?')) {
      return;
    }

    try {
      await projetService.deleteDossier(selectedProjet.id, dossierId);
      toast.success('Dossier supprimé');
      await loadDossiers(selectedProjet.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement des projets...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Gestion des Projets (Lots)
          </h2>
          <p className="text-slate-600">
            Gérez les projets et assignez les intervenants
          </p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          className="bg-blue-600 hover:bg-blue-700 rounded-xl"
          data-testid="create-projet-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Projet
        </Button>
      </div>

      {/* Liste des projets */}
      {projets.length === 0 ? (
        <Card className="bg-slate-50 border-slate-200 rounded-3xl">
          <CardContent className="p-12 text-center">
            <Folder className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun projet créé</h3>
            <p className="text-slate-500 mb-4">
              Créez votre premier projet pour commencer à organiser vos documents et vos intervenants.
            </p>
            <Button onClick={handleOpenCreate} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Créer un projet
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projets.map(projet => (
            <Card 
              key={projet.id}
              className={`bg-white border-slate-200 rounded-3xl shadow-sm hover:shadow-md transition-all ${
                selectedProjet?.id === projet.id ? 'ring-2 ring-blue-500' : ''
              }`}
              data-testid={`projet-card-${projet.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div 
                    className="flex items-center gap-3 cursor-pointer flex-1"
                    onClick={() => handleSelectProjet(projet)}
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <FolderOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {projet.nom}
                        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${
                          selectedProjet?.id === projet.id ? 'rotate-90' : ''
                        }`} />
                      </CardTitle>
                      {projet.description && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {projet.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Intervenants assignés */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Intervenants assignés
                  </p>
                  {projet.intervenants_ids?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {projet.intervenants_ids.map(animId => (
                        <Badge 
                          key={animId}
                          className="bg-blue-100 text-blue-700"
                        >
                          <User className="w-3 h-3 mr-1" />
                          {getAnimateurNom(animId)}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Aucun intervenant assigné</p>
                  )}
                </div>

                {/* Section Dossiers (expandable) */}
                {selectedProjet?.id === projet.id && (
                  <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Folder className="w-4 h-4 text-blue-600" />
                        Dossiers du projet
                      </p>
                      <Button 
                        size="sm" 
                        onClick={handleOpenCreateDossier}
                        className="h-7 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs"
                        data-testid="create-dossier-btn"
                      >
                        <FolderPlus className="w-3 h-3 mr-1" />
                        Nouveau
                      </Button>
                    </div>
                    
                    {loadingDossiers ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                      </div>
                    ) : dossiers.length === 0 ? (
                      <p className="text-sm text-slate-500 italic text-center py-2">
                        Aucun dossier créé
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {dossiers.map(dossier => (
                          <div 
                            key={dossier.id}
                            className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100 hover:border-blue-200 transition-colors"
                            data-testid={`dossier-item-${dossier.id}`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{dossier.nom}</p>
                                {dossier.description && (
                                  <p className="text-xs text-slate-500 truncate">{dossier.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenEditDossier(dossier)}
                                className="h-7 w-7 p-0 hover:bg-slate-100 rounded-lg"
                                data-testid={`edit-dossier-${dossier.id}`}
                              >
                                <Pencil className="w-3 h-3 text-slate-500" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteDossier(dossier.id)}
                                className="h-7 w-7 p-0 hover:bg-red-50 text-red-500 rounded-lg"
                                data-testid={`delete-dossier-${dossier.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Statistiques (quand pas expandé) */}
                {selectedProjet?.id !== projet.id && (
                  <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                    <span className="flex items-center gap-1 cursor-pointer hover:text-blue-600" onClick={() => handleSelectProjet(projet)}>
                      <Folder className="w-4 h-4" />
                      Voir les dossiers
                    </span>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProjet(projet);
                        loadDossiers(projet.id);
                        handleOpenCreateDossier();
                      }}
                      className="h-7 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs"
                      data-testid={`quick-add-dossier-${projet.id}`}
                    >
                      <FolderPlus className="w-3 h-3 mr-1" />
                      + Dossier
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleOpenEdit(projet)}
                    className="flex-1 rounded-xl"
                    data-testid={`edit-projet-${projet.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(projet.id)}
                    className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-projet-${projet.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog création/édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              {editingProjet ? 'Modifier le projet' : 'Nouveau projet'}
            </DialogTitle>
            <DialogDescription>
              {editingProjet 
                ? 'Modifiez les informations et les assignations du projet' 
                : 'Créez un nouveau projet et assignez-y des intervenants'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Nom du projet */}
            <div className="space-y-2">
              <Label className="font-semibold">Nom du projet *</Label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Lot 1 - Saint-Pierre"
                className="rounded-xl"
                data-testid="projet-nom-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du projet..."
                className="rounded-xl min-h-[80px]"
              />
            </div>

            {/* Sélection des intervenants */}
            <div className="space-y-3">
              <Label className="font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" />
                Intervenants assignés
              </Label>
              {animateurs.length === 0 ? (
                <p className="text-sm text-slate-500 italic">
                  Aucun intervenant disponible. Créez d'abord des utilisateurs avec le rôle "Intervenant".
                </p>
              ) : (
                <div className="bg-slate-50 rounded-xl p-3 max-h-[200px] overflow-y-auto space-y-2">
                  {animateurs.map(animateur => (
                    <div 
                      key={animateur.id}
                      className="flex items-center gap-3 p-2 hover:bg-white rounded-lg cursor-pointer transition-colors"
                      onClick={() => toggleAnimateur(animateur.id)}
                    >
                      <Checkbox
                        checked={formData.intervenants_ids?.includes(animateur.id)}
                        onCheckedChange={() => toggleAnimateur(animateur.id)}
                        data-testid={`animateur-checkbox-${animateur.id}`}
                      />
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-sm font-semibold">
                        {animateur.prenom?.[0]}{animateur.nom?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {animateur.prenom} {animateur.nom}
                        </p>
                        <p className="text-xs text-slate-500">{animateur.email}</p>
                      </div>
                      {formData.intervenants_ids?.includes(animateur.id) && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {formData.intervenants_ids?.length > 0 && (
                <p className="text-sm text-blue-600">
                  {formData.intervenants_ids.length} intervenant(s) sélectionné(s)
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
              data-testid="submit-projet-btn"
            >
              {submitting ? 'Enregistrement...' : (editingProjet ? 'Mettre à jour' : 'Créer le projet')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog création/édition de dossier */}
      <Dialog open={showDossierDialog} onOpenChange={setShowDossierDialog}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-amber-500" />
              {editingDossier ? 'Modifier le dossier' : 'Nouveau dossier'}
            </DialogTitle>
            <DialogDescription>
              {selectedProjet && (
                <span>Dans le projet : <strong>{selectedProjet.nom}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-semibold">Nom du dossier *</Label>
              <Input
                value={dossierFormData.nom}
                onChange={(e) => setDossierFormData({ ...dossierFormData, nom: e.target.value })}
                placeholder="Ex: Documents administratifs"
                className="rounded-xl"
                data-testid="dossier-nom-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (optionnel)</Label>
              <Textarea
                value={dossierFormData.description}
                onChange={(e) => setDossierFormData({ ...dossierFormData, description: e.target.value })}
                placeholder="Description du dossier..."
                className="rounded-xl min-h-[60px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDossierDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitDossier}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 rounded-xl"
              data-testid="submit-dossier-btn"
            >
              {submitting ? 'Enregistrement...' : (editingDossier ? 'Mettre à jour' : 'Créer le dossier')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjetsManager;
